import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Search, ShieldCheck, ShieldAlert, User2, Package, Receipt, Trash2, Image as ImgIcon } from "lucide-react";
import { api, API_BASE, errorToMessage, getStoredAuth } from "@/lib/apiClient";
import { canCasesAuthor, useAuth } from "@/lib/auth";
import { CASES } from "@/constants/testIds";

const WARRANTY_TONES = {
  IN_WARRANTY: "text-emerald-700 bg-emerald-50",
  BATTERY_COVERED: "text-teal-700 bg-teal-50",
  OUT_OF_WARRANTY: "text-slate-600 bg-slate-50",
  PHYSICAL_DAMAGE: "text-rose-700 bg-rose-50",
  ACCIDENTAL_DAMAGE: "text-orange-700 bg-orange-50",
  UNKNOWN: "text-slate-500 bg-slate-50",
};

export default function NewCasePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const allowed = canCasesAuthor(user);
  useEffect(() => {
    if (!allowed) navigate("/admin/cases", { replace: true });
  }, [allowed, navigate]);

  const [q, setQ] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [found, setFound] = useState(null);

  const [caseType, setCaseType] = useState("COMPLAINT");
  const [priority, setPriority] = useState("MEDIUM");
  const [category, setCategory] = useState("");
  const [problem, setProblem] = useState("");
  const [remarks, setRemarks] = useState("");
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const runSearch = async () => {
    if (!q.trim()) return;
    setSearching(true);
    setSearchError(null);
    setFound(null);
    try {
      const { data } = await api.get("/cases/search", {
        params: { q: q.trim() },
      });
      setFound(data);
      if (!data.customer) {
        setSearchError(
          "No customer matched. You can still create the case if you have a customer id.",
        );
      }
    } catch (e) {
      setSearchError(errorToMessage(e, "Search failed"));
    } finally {
      setSearching(false);
    }
  };

  const onUpload = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    setUploading(true);
    setError(null);
    try {
      const stored = getStoredAuth();
      const token = stored?.access_token;
      const uploaded = [];
      for (const file of fileList) {
        const form = new FormData();
        form.append("file", file);
        const resp = await fetch(`${API_BASE}/cases/images/upload`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: form,
        });
        if (!resp.ok) throw new Error(`Upload failed for ${file.name}`);
        uploaded.push(await resp.json());
      }
      setImages((prev) => [...prev, ...uploaded]);
    } catch (e) {
      setError(errorToMessage(e, "Image upload failed"));
    } finally {
      setUploading(false);
    }
  };

  const canSubmit = !!found?.customer && problem.trim().length > 0 && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        customer_id: found.customer.id,
        passport_id: found.passport?.id || null,
        sales_order_id: found.sales_order?.id || null,
        invoice_no: found.sales_order?.invoice_no || null,
        mobile: found.customer?.mobile || null,
        case_type: caseType,
        priority,
        complaint_category: category || null,
        problem_description: problem.trim(),
        remarks: remarks.trim() || null,
        images: images.map((i) => ({
          storage_key: i.storage_key,
          filename: i.filename,
          content_type: i.content_type,
          size_bytes: i.size_bytes,
        })),
      };
      const { data } = await api.post("/cases", payload);
      navigate(`/admin/cases/${data.id}`);
    } catch (e) {
      setError(errorToMessage(e, "Could not create case"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6 pb-10" data-testid="new-case-page">
      <div>
        <button onClick={() => navigate("/admin/cases")}
          className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 mb-2">
          <ArrowLeft size={14} /> Cases
        </button>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">New Customer Case</h1>
        <p className="text-sm text-slate-500 mt-1">
          Search by invoice, SO no., passport, barcode, serial, mobile or customer name — everything is auto-filled.
        </p>
      </div>

      {/* Universal search */}
      <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm space-y-4">
        <h2 className="text-sm font-semibold text-slate-900">Search customer / unit</h2>
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-2.5 text-slate-400" />
            <input data-testid={CASES.searchQ}
              value={q} onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); runSearch(); } }}
              placeholder="Invoice / SO / Passport / Barcode / Serial / Mobile / Name"
              className="w-full rounded-md border border-slate-200 pl-9 pr-3 py-2 text-sm outline-none focus:ring-2 focus:ring-slate-800" />
          </div>
          <button data-testid={CASES.searchBtn} type="button" onClick={runSearch}
            disabled={searching}
            className="inline-flex items-center gap-1 rounded-md bg-slate-900 px-3 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-40">
            <Search size={14} /> Search
          </button>
        </div>
        {searchError && (
          <div className="text-xs text-amber-700">{searchError}</div>
        )}

        {found && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-2">
            <div data-testid={CASES.searchCustomer}
              className="rounded-md border border-slate-100 p-3">
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-400 mb-2">
                <User2 size={12} /> Customer
              </div>
              {found.customer ? (
                <>
                  <div className="text-slate-900 font-medium">{found.customer.full_name}</div>
                  <div className="text-xs text-slate-600 font-mono">{found.customer.mobile}</div>
                  {found.customer.email && (
                    <div className="text-xs text-slate-500">{found.customer.email}</div>
                  )}
                </>
              ) : (
                <div className="text-sm text-slate-400">Not found</div>
              )}
            </div>
            <div data-testid={CASES.searchPassport}
              className="rounded-md border border-slate-100 p-3">
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-400 mb-2">
                <Package size={12} /> Passport
              </div>
              {found.passport ? (
                <>
                  <div className="text-slate-900 font-medium">{found.passport.brand} {found.passport.model}</div>
                  <div className="text-xs text-slate-600 font-mono">{found.passport.internal_barcode}</div>
                  <div className="text-xs text-slate-500 font-mono">{found.passport.manufacturer_serial}</div>
                  <div className="text-xs text-slate-500 mt-1">Status: {found.passport.current_status}</div>
                </>
              ) : (
                <div className="text-sm text-slate-400">No linked passport</div>
              )}
            </div>
            <div data-testid={CASES.searchSalesOrder}
              className="rounded-md border border-slate-100 p-3">
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-400 mb-2">
                <Receipt size={12} /> Sales Order
              </div>
              {found.sales_order ? (
                <>
                  <div className="text-slate-900 font-medium font-mono">{found.sales_order.so_no}</div>
                  <div className="text-xs text-slate-500 font-mono">Invoice: {found.sales_order.invoice_no || "—"}</div>
                  <div className="text-xs text-slate-500">{found.sales_order.status}</div>
                </>
              ) : (
                <div className="text-sm text-slate-400">No sales order</div>
              )}
            </div>
            <div data-testid={CASES.searchWarranty}
              className={`md:col-span-3 rounded-md border border-slate-100 p-3 flex items-center gap-3 ${found.warranty_status ? WARRANTY_TONES[found.warranty_status] : ""}`}>
              {found.warranty_status === "IN_WARRANTY" || found.warranty_status === "BATTERY_COVERED"
                ? <ShieldCheck size={16} />
                : <ShieldAlert size={16} />}
              <div className="text-sm font-semibold">
                {found.warranty_status ? found.warranty_status.replace("_", " ") : "Warranty unknown"}
              </div>
              {found.warranty_snapshot?.warranty_ends_on && (
                <div className="text-xs text-slate-600">
                  Ends {found.warranty_snapshot.warranty_ends_on}
                </div>
              )}
              {found.warranty_snapshot?.customer_warranty_expires_on && !found.warranty_snapshot?.warranty_ends_on && (
                <div className="text-xs text-slate-600">
                  Passport expiry: {found.warranty_snapshot.customer_warranty_expires_on}
                </div>
              )}
              {found.warranty_snapshot?.plan_name && (
                <div className="text-xs text-slate-500">Plan: {found.warranty_snapshot.plan_name}</div>
              )}
            </div>
          </div>
        )}
      </section>

      {/* Case details */}
      <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm space-y-4">
        <h2 className="text-sm font-semibold text-slate-900">Case details</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <label className="block">
            <span className="block text-xs font-medium text-slate-500 mb-1">Type *</span>
            <select data-testid={CASES.newType}
              value={caseType} onChange={(e) => setCaseType(e.target.value)}
              className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm">
              {["COMPLAINT","RETURN","REPLACEMENT","WARRANTY_CLAIM","REPAIR_REQUEST"].map((t) => (
                <option key={t} value={t}>{t.replace("_", " ")}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-slate-500 mb-1">Priority</span>
            <select data-testid={CASES.newPriority}
              value={priority} onChange={(e) => setPriority(e.target.value)}
              className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm">
              {["LOW","MEDIUM","HIGH","URGENT"].map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-slate-500 mb-1">Complaint category</span>
            <input data-testid={CASES.newCategory}
              value={category} onChange={(e) => setCategory(e.target.value)}
              placeholder="e.g., Battery, Display, Keyboard"
              className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" />
          </label>
        </div>
        <label className="block">
          <span className="block text-xs font-medium text-slate-500 mb-1">Problem description *</span>
          <textarea data-testid={CASES.newProblem}
            value={problem} onChange={(e) => setProblem(e.target.value)}
            rows={4} required
            placeholder="Describe the problem the customer is reporting"
            className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" />
        </label>
        <label className="block">
          <span className="block text-xs font-medium text-slate-500 mb-1">Remarks</span>
          <textarea data-testid={CASES.newRemarks}
            value={remarks} onChange={(e) => setRemarks(e.target.value)}
            rows={2} placeholder="Optional internal notes"
            className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" />
        </label>

        {/* Images */}
        <div>
          <div className="text-xs font-medium text-slate-500 mb-2">Images (up to 10)</div>
          <div className="flex flex-wrap gap-2 items-start">
            {images.map((img, idx) => (
              <div key={img.storage_key || `img-${idx}`} data-testid={CASES.newImagePreview(idx)}
                className="relative rounded-md border border-slate-200 p-2 pr-6 text-xs flex items-center gap-2">
                <ImgIcon size={12} />
                <span className="max-w-[10rem] truncate">{img.filename}</span>
                <button data-testid={CASES.newImageRemove(idx)}
                  onClick={() => setImages((prev) => prev.filter((_, i) => i !== idx))}
                  className="absolute top-1 right-1 text-slate-400 hover:text-rose-600">
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
            {images.length < 10 && (
              <label className="inline-flex items-center gap-2 rounded-md border border-dashed border-slate-300 px-3 py-2 text-xs text-slate-600 hover:bg-slate-50 cursor-pointer">
                <input type="file" accept="image/*" multiple className="hidden"
                  data-testid={CASES.newImageUpload}
                  onChange={(e) => onUpload(Array.from(e.target.files || []))} />
                {uploading ? "Uploading…" : "+ Add image"}
              </label>
            )}
          </div>
        </div>
      </section>

      {error && (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      )}

      <div className="flex items-center justify-end gap-2">
        <button data-testid={CASES.newCancel} onClick={() => navigate("/admin/cases")}
          className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm text-slate-700 hover:bg-slate-50">
          Cancel
        </button>
        <button data-testid={CASES.newSubmit} disabled={!canSubmit}
          onClick={submit}
          className="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-40">
          {submitting ? "Creating…" : "Create case"}
        </button>
      </div>
    </div>
  );
}
